/**
 * Two Meta (Facebook) middlewares live here:
 *
 *  1. `registerFacebookSignalsMiddleware` — a SOURCE middleware that attaches
 *     the click/browser identifiers (`_fbc` / `_fbp`) to every Segment message
 *     so the cloud-mode Facebook Conversions API destination can match
 *     purchases back to the ad click. These are non-PII click ids and are fine
 *     to broadcast to every destination.
 *
 *  2. `registerFacebookPixelMatchMiddleware` — a DESTINATION middleware scoped
 *     to the device-mode Facebook Pixel destination that adds the shopper's
 *     billing identity (email / phone / name / city / state / postcode) as
 *     advanced-matching keys. It is destination-scoped ON PURPOSE: this is PII,
 *     and a source middleware would leak it into every event sent to every
 *     other destination (Klaviyo, HubSpot, GA). See that function for the
 *     mechanism.
 *
 * The click ids `_fbc` / `_fbp` are set on the storefront (www.vinomofo.com) —
 * the pixel and the edge `?fbclid` capture there write them scoped to the
 * registrable domain (`.vinomofo.com`), so they are readable here on the
 * checkout subdomain (checkoutsetup.vinomofo.com / checkoutb.vinomofo.com.sg).
 * They are stamped in two places to stay robust to whichever mapping the
 * Segment Facebook destination is configured with:
 *   - `properties.fbc` / `properties.fbp` on track/page (the Actions CAPI
 *     destination's default source), and
 *   - `context.traits.fbc` / `context.traits.fbp` (advanced-matching fallback).
 */

var signalsMiddlewareRegistered = false;
var pixelMatchMiddlewareRegistered = false;

// The Segment integration name of the device-mode Facebook Pixel destination,
// used to scope the advanced-matching middleware to it alone.
var FACEBOOK_PIXEL_DESTINATION = "Facebook Pixel";
// Latest billing identity, pushed in by the checkout as the shopper enters it
// client-side. Module-level (mirroring the cookie reads below) so the
// middleware can stamp it onto outgoing messages without threading React state
// through the Segment queue.
var identityMatchKeys = {};

/**
 * Feed the shopper's billing identity to the device-mode Facebook Pixel's
 * advanced matching.
 *
 * The pixel previously sent almost nothing: the only `identify()` in the app
 * fires for signed-in customers and carries email but never phone, and guests
 * are never identified at all — so the browser pixel saw `em` for members only
 * and nothing else for anyone, while the cloud CAPI stream already had the
 * full identity block off the Order Completed properties. The checkout calls
 * this as billing details are entered (guests included), closing that gap.
 *
 * The KEY NAMES here must match the destination's "Allowed PII Properties"
 * whitelist exactly (flat, not nested) — Segment strips any billing PII from
 * event properties that is not on that list. Today that list is: email,
 * firstName, lastName, city, state, postalCode, phone, country — so any field
 * added here must also be added as a row in that destination setting, or it
 * gets stripped.
 *
 * Pass RAW values — the Facebook Pixel hashes them on the way out, so
 * pre-hashing here would double-hash. Mirror the CAPI billing values (E.164
 * phone, full state name, full country name) verbatim so the device pixel and
 * the cloud CAPI hash the same strings and Meta dedups the two streams. Pass
 * `undefined` to clear.
 *
 * NOTE: Meta's `country` advanced-matching field officially expects a 2-letter
 * ISO code (e.g. `au`), not a full name. We send the full name to stay
 * identical to the CAPI stream; if country matching needs to actually fire at
 * Meta, normalise to ISO2 on BOTH streams (here and CAPI `mapAddress`).
 */
export var setFacebookIdentityMatchKeys = function setFacebookIdentityMatchKeys(keys) {
  identityMatchKeys = {
    email: keys.email || undefined,
    phone: keys.phone || undefined,
    firstName: keys.firstName || undefined,
    lastName: keys.lastName || undefined,
    city: keys.city || undefined,
    state: keys.state || undefined,
    postalCode: keys.postalCode || undefined,
    country: keys.country || undefined
  };
};
var readCookie = function readCookie(name) {
  if (typeof document === "undefined") return undefined;
  var match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
};

// Meta's default lifetime for the _fbc / _fbp identifiers (90 days).
var FBC_FBP_MAX_AGE_S = 90 * 24 * 60 * 60;

/**
 * Registrable-domain scope so a seeded cookie is readable across the checkout
 * subdomains and the confirmation page where "Order Completed" fires. Returns
 * `undefined` (host-only cookie) for preview / localhost hosts the browser
 * would reject a domain-scoped cookie on.
 */
var checkoutCookieDomain = function checkoutCookieDomain(hostname) {
  var h = hostname.toLowerCase();
  if (h === "vinomofo.com.sg" || h.endsWith(".vinomofo.com.sg")) return ".vinomofo.com.sg";
  if (h === "vinomofo.com" || h.endsWith(".vinomofo.com")) return ".vinomofo.com";
  return undefined;
};
var writeCookie = function writeCookie(name, value) {
  var domain = checkoutCookieDomain(window.location.hostname);
  var parts = ["".concat(name, "=").concat(encodeURIComponent(value)), "path=/", "max-age=".concat(FBC_FBP_MAX_AGE_S), "samesite=lax", "secure"];
  if (domain) parts.push("domain=".concat(domain));
  document.cookie = parts.join("; ");
};

/**
 * Seed `_fbc` / `_fbp` from the checkout handoff URL onto the checkout domain.
 *
 * WhiskyMofo browses on `www.whiskymofo.com`, where the `?fbclid` capture writes
 * `_fbc` scoped to `.whiskymofo.com`. The checkout runs on a DIFFERENT
 * registrable domain (`checkoutsetup.vinomofo.com`), so that cookie is never
 * sent with the handoff and Purchase reached Meta with ~9% `fbc` coverage while
 * on-site AddToCart / ViewContent (which fire on the storefront domain) still
 * carried it. (`_fbp` stayed high because the checkout pixel mints its own; only
 * `_fbc`, which can't be regenerated without the click, collapsed.)
 *
 * The storefront now forwards the identifiers as `?_fbc` / `?_fbp`; copy them
 * into a first-party cookie here so the source middleware below attaches them to
 * every event on this domain, including the confirmation-page "Order Completed".
 *
 * Only seed when the cookie is ABSENT, so we never clobber a `_fbp` the checkout
 * pixel already minted on this domain. Vinomofo's same-domain handoff is
 * untouched: its `.vinomofo.com` cookies already flow, so no param is forwarded
 * and nothing is seeded.
 */
var hydrateFacebookSignalsFromUrl = function hydrateFacebookSignalsFromUrl() {
  if (typeof window === "undefined") return;
  var params;
  try {
    params = new URL(window.location.href).searchParams;
  } catch (_unused) {
    return;
  }
  ["_fbc", "_fbp"].forEach(function (name) {
    var fromUrl = params.get(name);
    if (fromUrl && !readCookie(name)) writeCookie(name, fromUrl);
  });
};
var messageType = function messageType(payload) {
  var _payload$obj$type, _payload$obj;
  return typeof payload.type === "function" ? payload.type() : (_payload$obj$type = (_payload$obj = payload.obj) === null || _payload$obj === void 0 ? void 0 : _payload$obj.type) !== null && _payload$obj$type !== void 0 ? _payload$obj$type : payload.type;
};

// ── 1. Source middleware: _fbc / _fbp click ids (broadcast to all) ──────────

var injectFacebookSignals = function injectFacebookSignals(payload, next) {
  var fbc = readCookie("_fbc");
  var fbp = readCookie("_fbp");

  // Nothing to add — pass through untouched.
  if (!fbc && !fbp) {
    next(payload);
    return;
  }
  var obj = payload.obj || payload;
  var type = messageType(payload);
  var context = obj.context || {};
  var traits = context.traits || {};
  if (fbc) traits.fbc = fbc;
  if (fbp) traits.fbp = fbp;
  context.traits = traits;
  obj.context = context;
  if (type === "track" || type === "page") {
    var properties = obj.properties || {};
    if (fbc) properties.fbc = fbc;
    if (fbp) properties.fbp = fbp;
    obj.properties = properties;
  }
  next(payload);
};
export var registerFacebookSignalsMiddleware = function registerFacebookSignalsMiddleware() {
  if (typeof window === "undefined") return;

  // Seed the click id from the handoff URL first, independent of whether
  // analytics.js has loaded yet, so the cookie exists before the first event.
  hydrateFacebookSignalsFromUrl();
  if (signalsMiddlewareRegistered) return;
  var analytics = window.analytics;
  if (!analytics) return;
  if (typeof analytics.addSourceMiddleware === "function") {
    analytics.addSourceMiddleware(function (_ref) {
      var payload = _ref.payload,
        next = _ref.next;
      return injectFacebookSignals(payload, next);
    });
    signalsMiddlewareRegistered = true;
    return;
  }
  if (typeof analytics.addMiddleware === "function") {
    analytics.addMiddleware(function (_ref2) {
      var payload = _ref2.payload,
        next = _ref2.next;
      return injectFacebookSignals(payload, next);
    });
    signalsMiddlewareRegistered = true;
  }
};

// ── 2. Destination middleware: advanced-matching PII (Facebook Pixel only) ──

var injectFacebookPixelMatch = function injectFacebookPixelMatch(payload, next) {
  var _identityMatchKeys = identityMatchKeys,
    email = _identityMatchKeys.email,
    phone = _identityMatchKeys.phone,
    firstName = _identityMatchKeys.firstName,
    lastName = _identityMatchKeys.lastName,
    city = _identityMatchKeys.city,
    state = _identityMatchKeys.state,
    postalCode = _identityMatchKeys.postalCode,
    country = _identityMatchKeys.country;

  // Nothing to add — pass through untouched.
  if (!email && !phone && !firstName && !lastName && !city && !state && !postalCode && !country) {
    next(payload);
    return;
  }
  var type = messageType(payload);
  // Advanced matching rides on the *properties* of track/page events — the
  // Facebook Pixel destination reads billing PII from there (gated by its
  // "Allowed PII Properties" whitelist), not from context.traits. Flat keys,
  // matched to the whitelist by name.
  if (type === "track" || type === "page") {
    var obj = payload.obj || payload;
    var properties = obj.properties || {};
    // Only fill gaps — never clobber a value the event already carries.
    if (email && properties.email === undefined) properties.email = email;
    if (phone && properties.phone === undefined) properties.phone = phone;
    if (firstName && properties.firstName === undefined) properties.firstName = firstName;
    if (lastName && properties.lastName === undefined) properties.lastName = lastName;
    if (city && properties.city === undefined) properties.city = city;
    if (state && properties.state === undefined) properties.state = state;
    if (postalCode && properties.postalCode === undefined) properties.postalCode = postalCode;
    if (country && properties.country === undefined) properties.country = country;
    obj.properties = properties;
  }
  next(payload);
};
export var registerFacebookPixelMatchMiddleware = function registerFacebookPixelMatchMiddleware() {
  if (typeof window === "undefined") return;
  if (pixelMatchMiddlewareRegistered) return;
  var analytics = window.analytics;
  if (!analytics) return;
  if (typeof analytics.addDestinationMiddleware === "function") {
    analytics.addDestinationMiddleware(FACEBOOK_PIXEL_DESTINATION, [function (_ref3) {
      var payload = _ref3.payload,
        next = _ref3.next;
      return injectFacebookPixelMatch(payload, next);
    }]);
    pixelMatchMiddlewareRegistered = true;
  }
};